export const MODAL_LAYER_BASE = 1300;
export const MODAL_LAYER_STEP = 10;

/** Fixed z-index for all app dropdowns, menus, autocompletes, and date pickers. */
export const APP_DROPDOWN_Z_INDEX = 1400;

const APP_DROPDOWN_Z_INDEX_VAR = "--app-dropdown-z-index";

/** Dropdown z-index that stays above the topmost open modal layer. */
export const APP_DROPDOWN_Z_INDEX_CSS = `var(${APP_DROPDOWN_Z_INDEX_VAR}, ${APP_DROPDOWN_Z_INDEX})`;

/** Minimum z-index for platform connection backdrops (above dropdowns). */
export const CONNECTION_BACKDROP_Z_INDEX = APP_DROPDOWN_Z_INDEX + 100;

/** @type {{ id: string, zIndex: number }[]} */
let openModalLayers = [];

/** @type {Set<() => void>} */
const modalLayerSubscribers = new Set();
let modalLayerNotifyHandle = null;

const notifyModalLayers = () => {
  if (modalLayerNotifyHandle != null) return;
  // Defer so a register/unregister during render does not setState in another
  // component before this render finishes. Repeat calls in the same turn collapse.
  modalLayerNotifyHandle = setTimeout(() => {
    modalLayerNotifyHandle = null;
    modalLayerSubscribers.forEach((subscriber) => {
      try {
        subscriber();
      } catch {
        // One subscriber must not block the others.
      }
    });
  }, 0);
};

export const subscribeModalLayers = (subscriber) => {
  if (typeof subscriber !== "function") return () => {};
  modalLayerSubscribers.add(subscriber);
  return () => {
    modalLayerSubscribers.delete(subscriber);
  };
};

export const registerModalLayer = (modalId, explicitZIndex) => {
  const existing = openModalLayers.find((layer) => layer.id === modalId);
  if (existing) {
    if (explicitZIndex != null) {
      const didChange = existing.zIndex !== explicitZIndex;
      existing.zIndex = explicitZIndex;
      syncDropdownZIndex();
      if (didChange) notifyModalLayers();
    }
    return existing.zIndex;
  }

  const topZIndex = getMaxModalLayerZIndex();
  const stackedZIndex =
    topZIndex > 0 ? topZIndex + MODAL_LAYER_STEP : MODAL_LAYER_BASE;
  const zIndex = explicitZIndex ?? stackedZIndex;
  openModalLayers.push({ id: modalId, zIndex });
  syncDropdownZIndex();
  notifyModalLayers();
  return zIndex;
};

export const unregisterModalLayer = (modalId) => {
  const nextLayers = openModalLayers.filter((layer) => layer.id !== modalId);
  const didRemove = nextLayers.length !== openModalLayers.length;
  openModalLayers = nextLayers;
  syncDropdownZIndex();
  if (didRemove) notifyModalLayers();
};

const syncDropdownZIndex = () => {
  if (typeof document === "undefined") return;
  const dropdownZIndex = Math.max(
    APP_DROPDOWN_Z_INDEX,
    getMaxModalLayerZIndex() + MODAL_LAYER_STEP / 2
  );
  document.documentElement.style.setProperty(
    APP_DROPDOWN_Z_INDEX_VAR,
    String(dropdownZIndex)
  );
};

export const getMaxModalLayerZIndex = () => {
  if (openModalLayers.length === 0) {
    return 0;
  }

  return Math.max(...openModalLayers.map((layer) => layer.zIndex));
};

/** z-index for connection backdrops: always above the topmost open modal layer. */
export const getConnectionBackdropZIndex = () => {
  const topModalZIndex = getMaxModalLayerZIndex();
  const dynamicZIndex =
    topModalZIndex > 0
      ? topModalZIndex + MODAL_LAYER_STEP
      : CONNECTION_BACKDROP_Z_INDEX;

  return Math.max(dynamicZIndex, CONNECTION_BACKDROP_Z_INDEX);
};

/** Minimum z-index for Google Picker (above gallery, dropdowns, and connection backdrops). */
export const GOOGLE_PICKER_Z_INDEX_MIN =
  CONNECTION_BACKDROP_Z_INDEX + MODAL_LAYER_STEP;

/** z-index for Google Picker: always above the topmost open modal layer. */
export const getGooglePickerZIndex = () => {
  const topModalZIndex = getMaxModalLayerZIndex();
  const stackedAboveModal =
    topModalZIndex > 0
      ? topModalZIndex + MODAL_LAYER_STEP
      : MODAL_LAYER_BASE + MODAL_LAYER_STEP;

  return Math.max(stackedAboveModal, GOOGLE_PICKER_Z_INDEX_MIN);
};
